import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Select, Button, Input, Spin } from 'antd'
import MultiSelector from './MultiSelector'
import SingleSelector from './SingleSelector'
import ChartPanel from './ChartPanel'
import PeriodRangePicker from './PeriodRangePicker'
import RosePlotSVG from './RosePlotSVG'
import dayjs from 'dayjs'
import useElements from '../hooks/useElements'
import useDevices from '../hooks/useDevices'
import { useDateRange } from '../hooks/useDateRange'
import { apiFetch } from "../utils/api";
import { formatBrowser } from "../utils/dateUtils"
import {
  getLatestReadings,
  getFilteredReadings,
  getPollutionRose,
} from "../services/readingService";

const tabList = [
  { key: 'parameter', label: 'Historical' },
  { key: 'rose', label: 'Pollution rose' },
]

const DeviceMapPanel = ({ device, open, onClose }) => {
  const [activeTab, setActiveTab] = useState('parameter')
  const [selectedParams, setSelectedParams] = useState([])
  const [selectedParam, setSelectedParam] = useState(null)
  const [customRange, setCustomRange] = useState({
    from: dayjs().startOf('day'), // 24 horas previas, comenzando desde el minuto 0
    to: dayjs().endOf('day') // El final del día actual
  })
  const [filteredReadings, setFilteredReadings] = useState([])
  const [roseData, setRoseData] = useState(null)
  const [loadingLatest, setLoadingLatest] = useState(false);   // Panel izquierdo
  const [loadingCharts, setLoadingCharts] = useState(false);   // Panel derecho
  const [periodicity, setPeriodicity] = useState('AVG5M')  // Estado para la periodicidad (agregación)
  const [periodicityRose, setPeriodicityRose] = useState('AVG5M')  // Estado para la periodicidad (agregación)
  const [dateRange, setDateRange] = useState('7d')  // Estado para elegir entre 7 días o 1 mes
  const [lastReadings, setLastReadings] = useState([])
  const [breakPoints, setBreakPoints] = useState(null)
  const periodPickerRef = useRef()

  // Usamos el hook useElements para obtener los parámetros
  const { elements: allElements, loading: loadingElements, error } = useElements(device ? [device.id] : []);
  const { elements: gasElements } = useElements(device ? [device.id] : [], true);

  const { devicesWithAnemometer } = useDevices();
  const anemometerDevices = devicesWithAnemometer.map(d => ({ id: d.id, name: d.name }));
  const [selectedAnemometerId, setSelectedAnemometerId] = useState(null);

  const {
    showCustom,
    selectedLabel,
    setPreset,
    setShowCustom,
    selectedPreset,
    presetDefs,
  } = useDateRange(setCustomRange);

  useEffect(() => {
    if (device) {
      setLoadingLatest(true);
      getLatestReadings(device.id)
        .then(setLastReadings)
        .catch((err) => {
          console.error("Failed to load latest readings", err);
          setLastReadings([]);
        })
        .finally(() => setLoadingLatest(false));
    }
  }, [device]);

  const chartSeries = useMemo(() => {
    const seriesByParam = {};
    filteredReadings.forEach(r => {
      const key = r.elementName || r.element_name || r.name || r.elementId || r.element_id;
      if (!seriesByParam[key]) seriesByParam[key] = [];
      seriesByParam[key].push({
        date: r.readDate || r.lastReadDate || r.last_read_date,
        value: Number(r.readValue ?? r.lastReadValue ?? r.last_read_value),
        unit: r.unit || ''
      });
    });
    Object.values(seriesByParam).forEach(arr => arr.sort((a, b) => new Date(a.date) - new Date(b.date)));
    return Object.entries(seriesByParam).map(([key, arr]) => ({
      name: key,
      data: arr.map(d => [new Date(d.date).getTime(), d.value]),
      unit: arr[0]?.unit || ''
    }));
  }, [filteredReadings]);

  // Lógica para manejar la búsqueda
  const handleSearch = () => {
    const now = new Date();
    const from = new Date(now);
    if (dateRange === "7d") from.setDate(now.getDate() - 7);
    else if (dateRange === "1m") from.setMonth(now.getMonth() - 1);

    setLoadingCharts(true);
    getFilteredReadings({
      deviceIds: [device.id],
      elementIds: selectedParams,
      from: from.toISOString(),
      to: now.toISOString(),
      periodicity,
    })
      .then(setFilteredReadings)
      .catch((err) => {
        console.error("Failed to load readings", err);
        setFilteredReadings([]);
      })
      .finally(() => setLoadingCharts(false));
  };

  useEffect(() => {
    if (selectedParams.length === 0) return; // No hacer nada si no hay parámetros seleccionados
    handleSearch(); // Ejecutar la búsqueda
  }, [selectedParams, dateRange, periodicity]);

  useEffect(() => {
    setActiveTab('parameter')
    setSelectedParams([])
    setSelectedParam(null)
    setFilteredReadings([])
    setRoseData(null)
    setLastReadings([])
  }, [device])

  const handleTabClick = (key) => {
    setActiveTab(key)
    setSelectedParams([])
    setSelectedParam(null)
    setFilteredReadings([])
    setRoseData(null)
    // setLastReadings([])
  }

  // Lógica para la pestaña de Pollution Rose
  const handleRoseSearch = () => {
    if (!selectedParam || !periodPickerRef.current) return;

    const { from, to } = periodPickerRef.current.getRange();
    setLoadingCharts(true);

    getPollutionRose({
      deviceIds: [device.id],
      elementId: selectedParam,
      startDate: from.toISOString(),
      endDate: to.toISOString(),
      anemometerDeviceId: selectedAnemometerId,
      periodicity: periodicityRose,
      breaks: breakPoints,
    })
      .then((data) => setRoseData(data.length > 0 ? data[0] : null))
      .catch((err) => {
        console.error("Failed to load rose data", err);
        setRoseData(null);
      })
      .finally(() => setLoadingCharts(false));
  };

  const RoseLegend = ({ data }) => {
    if (!Array.isArray(data) || data.length === 0) return null

    return (
      <div style={{
        background: '#fff',
        borderRadius: 4,
        boxShadow: '0 4px 16px 0 #0075C922',
        padding: '10px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        // fontFamily: 'Manrope, sans-serif',
        fontSize: 14,
        minWidth: 130,
        maxWidth: 170
      }}>
        {data.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 4,
              background: item.color,
              border: '1px solid #000',
              boxShadow: '0 0 0 1px #ccc'
            }} />
            <span style={{
              color: '#222',
              letterSpacing: 0.1,
              fontWeight: 500,
              minWidth: 50,
              whiteSpace: 'nowrap'
            }}>{decodeHtmlEntities(item.name)}</span>
          </div>
        ))}
      </div>
    )
  }

  function decodeHtmlEntities(str) {
    if (!str) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  if (!device || !open) return null

  return (
    <div style={{ position: 'fixed', left: 260, right: 0, bottom: 0, zIndex: 1001,
      background: 'rgba(247, 247, 247, 0.90)', boxShadow: '0 -16px 32px -8px #0075C944',
      display: 'flex', padding: '5px 10px', gap: 28, height: 317, minHeight: 160,
      maxHeight: '40vh', overflow: 'hidden', transition: 'height 0.2s'
    }}>
      {/* Lado izquierdo: device info */}
      <div style={{ minWidth: 400, maxWidth: 430, display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ color: '#0075C9', fontWeight: 900, fontSize: 19, marginBottom: 15, letterSpacing: 0.5, lineHeight: 1.1 }}>
          {device.name}
        </h2>
        <div style={{
          background: '#fff', borderRadius: 4, padding: '6px 4px', flex: 1,
          minHeight: 0, overflow: 'auto'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 4,
            padding: '6px 4px',
            flex: 1,
            minHeight: 0,
            overflow: 'auto'
          }}>
            {loadingLatest ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                padding: '20px 0'
              }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {lastReadings && lastReadings.length > 0 ? (
                  lastReadings.map((r, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '1.5fr 1fr 1fr',
                      alignItems: 'center',
                      padding: '3px 3px',
                      borderRadius: 6,
                      marginBottom: 1,
                      background: i % 2 ? '#F5FAFD' : 'white',
                      fontSize: 13,
                      minHeight: 22,
                    }}>
                      <div style={{ fontWeight: 500 }}>
                        {r.elementName || r.element_name || r.name}
                      </div>
                      <div style={{
                        fontWeight: 700,
                        color: '#0075C9',
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center'
                      }}>
                        {r.readValue ?? r.last_read_value ?? '--'}
                        <span style={{ fontSize: 11, marginLeft: 2 }}>{r.unit}</span>
                      </div>
                      <div style={{
                        color: '#888',
                        fontSize: 11,
                        textAlign: 'right'
                      }}>
                        {(r.readDate || r.last_read_date)
                          ? formatBrowser(r.readDate || r.last_read_date, { seconds: true })
                          : '--'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#bbb', fontSize: 13, textAlign: 'center' }}>
                    No recent readings
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* Lado derecho: tabs */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 340 }}>
        <div style={{ display: 'flex', gap: 30, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
          {tabList.map(tab =>
            <div key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              style={{
                color: activeTab === tab.key ? '#0075C9' : '#777',
                borderBottom: activeTab === tab.key ? '3px solid #0075C9' : '3px solid transparent',
                paddingBottom: 3, cursor: 'pointer'
              }}>
              {tab.label}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            border: 'none', background: 'none', fontSize: 25,
            color: '#bbb', cursor: 'pointer'
          }}>×</button>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.98)', borderRadius: 4,
          flex: 1, padding: '10px 10px 0px 10px', display: 'flex',
          flexDirection: activeTab === 'rose' ? 'row' : 'column', gap: 20, height: '100%'
        }}>
          {activeTab === 'parameter' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <MultiSelector
                  items={allElements}  // Usamos 'elements' del hook useElements
                  value={selectedParams}
                  onChange={setSelectedParams}
                  idKey="id" labelKey="name" placeholder="Select parameters"
                />
                
                {/* Selector de periodicidad */}
                <Select
                  value={periodicity}
                  onChange={setPeriodicity}
                  style={{ width: 100 }}
                  placeholder="Select aggregation"
                >
                  <Select.Option value="AVG5M">AVG 5M</Select.Option>
                  <Select.Option value="AVG15M">AVG 15M</Select.Option>
                  <Select.Option value="AVG1H">AVG 1H</Select.Option>
                  <Select.Option value="AVG24H">AVG 24H</Select.Option>
                </Select>
                <Select
                  value={dateRange}
                  onChange={setDateRange}
                  style={{ width: 150 }}
                  placeholder="Select range"
                >
                  <Select.Option value="7d">Last 7 days</Select.Option>
                  <Select.Option value="1m">Last 1 month</Select.Option>
                </Select>
                
                <div style={{ flex: 1 }} />
                
                {/* Selector de rango de fechas (7 días o 1 mes) */}
              </div>

              <div style={{ flex: 1, borderRadius: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: 0.2 }}>
                {loadingCharts ? <div style={{ color: '#bbb', fontSize: 14 }}>Loading...</div> :
                  (selectedParams.length > 0 && chartSeries.length > 0 ? (
                    <ChartPanel title={null} series={chartSeries} options={{ chart: { height: 200 }, legend: { enabled: true } }} />
                  ) : <div style={{ color: '#bbb', fontSize: 14, width: '100%', textAlign: 'center' }}>
                    Select one or more parameters to view the chart.
                  </div>)}
              </div>
            </>
          )}

          {activeTab === 'rose' && (
            <>
              {/* Pollution Rose content */}
              <div style={{display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 5 }}>
                <SingleSelector
                  items={gasElements}  // Usamos 'elements' para el selector de parámetro
                  value={selectedParam}
                  onChange={setSelectedParam}
                  idKey="id" labelKey="name" placeholder="Select parameter"
                />
                <Select
                  value={periodicityRose}
                  onChange={setPeriodicityRose}
                  // style={{ width: 100 }}
                  placeholder="Select aggregation"
                >
                  <Select.Option value="AVG5M">AVG 5M</Select.Option>
                  <Select.Option value="AVG15M">AVG 15M</Select.Option>
                  <Select.Option value="AVG1H">AVG 1H</Select.Option>
                  <Select.Option value="AVG24H">AVG 24H</Select.Option>
                </Select>
                <SingleSelector
                  items={anemometerDevices}
                  value={selectedAnemometerId}
                  onChange={setSelectedAnemometerId}
                  idKey="id"
                  labelKey="name"
                  placeholder="Select device with anemometer"
                />
                <PeriodRangePicker
                  ref={periodPickerRef}
                  value={customRange}
                  onChange={setCustomRange}
                  showCustom={showCustom}
                  setPreset={setPreset}
                  setShowCustom={setShowCustom}
                  selectedPreset={selectedPreset}
                  selectedLabel={selectedLabel}
                  presetDefs={presetDefs}
                />
                <Input
                  value={breakPoints}
                  onChange={e => setBreakPoints(e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Break points (optional, e.g. 2,5,8,14)"
                />
                <Button
                  style={{
                    // marginTop: 4,
                    background: '#0075C9',
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 12px',
                    cursor: 'pointer'
                  }}
                  onClick={handleRoseSearch}
                >
                  Load data
                </Button>
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start', // <- centrada verticalmente respecto al gráfico
                // height: '100%',
                gap: 0
              }}>
                <div style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 265
                }}>
                  {loadingCharts ? <div style={{ color: '#bbb', fontSize: 14 }}>Loading...</div> :
                    roseData ? (
                      <RosePlotSVG data={roseData.data} tag={roseData.name || roseData.serialNumber}
                        value={roseData.mean} calm={roseData.calm} width={350} height={275} />
                    ) : <div style={{ color: '#bbb', fontSize: 14, width: '100%', textAlign: 'center' }}>
                      Select parameter and date range to view the rose plot.
                    </div>}
                </div>
                {/* Leyenda justo al lado derecho del gráfico, centrada verticalmente */}
                {roseData && Array.isArray(roseData.data) && roseData.data.length > 0 && (
                  <div style={{
                    marginLeft: 22,
                    display: 'flex',
                    alignItems: 'flex-start', // puedes usar 'center' si prefieres la leyenda centrada
                    height: 'auto'
                  }}>
                    <RoseLegend data={roseData.data} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeviceMapPanel
